import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import GatedFeature from "../components/GatedFeature";
import { toast } from "sonner";
import { Plus, Sprout, Leaf } from "lucide-react";

export default function JournalScreen() {
  const { user } = useAuth();
  const [plants, setPlants] = useState([]);
  const [locked, setLocked] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/journals");
      setPlants(data.plants || []);
      setLocked(!!data.locked);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const addPlant = async (e) => {
    e.preventDefault();
    try {
      await api.post("/journals", { name: newName });
      setShowAdd(false);
      setNewName("");
      toast.success("Plant added to journal");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add plant");
    }
  };

  if (locked) {
    return (
      <Shell section="journal">
        <h1 className="text-2xl font-bold mb-5 text-shadow-green">Growth Journal</h1>
        <GatedFeature
          tier="platinum"
          title="Track your plants like a pro"
          subtitle="Upload weekly photos, get AI growth reports, and never miss a care adjustment. Available on Platinum."
        />
        <div className="mt-6 grid grid-cols-2 gap-3 opacity-60">
          {["Weekly comparison", "AI growth reports", "Download as ZIP", "3-day data retention"].map((f, i) => (
            <div key={i} className="glass-card p-4 text-center">
              <Sprout size={20} className="mx-auto text-[#4CAF50] mb-2" />
              <p className="text-xs text-soft">{f}</p>
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  return (
    <Shell section="journal">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-shadow-green">Growth Journal</h1>
        <span className="glass-pill purple">Platinum</span>
      </div>

      {plants.length === 0 ? (
        <div className="glass-card p-8 text-center fade-up" data-testid="journal-empty">
          <Sprout size={32} className="mx-auto text-[#4CAF50] mb-3" />
          <p className="text-soft mb-4">No plants tracked yet.</p>
          <button onClick={() => setShowAdd(true)} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> Add your first plant
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="journal-grid">
          {plants.map((p, idx) => (
            <button
              key={p.plant_id}
              onClick={() => navigate(`/journal/${p.plant_id}`)}
              className={`glass-card p-3 text-left fade-up stagger-${(idx % 5) + 1}`}
              data-testid={`journal-plant-${idx}`}
            >
              {p.thumbnail ? (
                <img src={p.thumbnail} className="aspect-square w-full rounded-lg object-cover" alt={p.name} />
              ) : (
                <div className="aspect-square w-full rounded-lg flex items-center justify-center"
                     style={{ background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.2)" }}>
                  <Leaf size={26} className="text-[#4CAF50]" />
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="w-2.5 h-2.5 rounded-full dot-pulse"
                  style={{
                    background: p.health_status === "red" ? "#D32F2F" : p.health_status === "amber" ? "#FF8F00" : "#4CAF50",
                    boxShadow: `0 0 8px ${p.health_status === "red" ? "#D32F2F" : p.health_status === "amber" ? "#FF8F00" : "#4CAF50"}`,
                  }}
                />
                <p className="text-sm font-semibold truncate flex-1">{p.name}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <button
        onClick={() => setShowAdd(true)}
        className="fixed bottom-24 right-5 rounded-full bg-[#4CAF50] flex items-center justify-center glow-pulse z-30 journal-fab"
        data-testid="journal-fab"
        style={{ width: 56, height: 56, boxShadow: "var(--glow-green-bright)" }}
      >
        <Plus size={26} className="text-white" />
        {/* Orbiting particles */}
        <span className="fab-orbit" aria-hidden>
          <span /><span /><span />
        </span>
      </button>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={() => setShowAdd(false)} data-testid="add-plant-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={addPlant} className="glass-card-strong w-full max-w-md p-6 m-2 mb-6 rounded-2xl">
            <h2 className="text-lg font-bold mb-4">Add a plant to track</h2>
            <input
              className="glass-input mb-4"
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nickname (e.g., Living Room Monstera)"
              required
              data-testid="add-plant-name-input"
            />
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-outline flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="add-plant-submit">Add Plant</button>
            </div>
          </form>
        </div>
      )}
    </Shell>
  );
}
